/**
 * Price Range Slider Component for Shopify
 * Works with both Desktop (facet-filters-form) and Mobile (facet-filters-form-mobile)
 */

if (!customElements.get('price-range')) {
  class PriceRange extends HTMLElement {
    constructor() {
      super();
      
      // Get all required elements
      this.minRangeSlider = this.querySelector('.range-min');
      this.maxRangeSlider = this.querySelector('.range-max');
      this.minNumberInput = this.querySelector('.price-min');
      this.maxNumberInput = this.querySelector('.price-max');
      this.activeBackground = this.querySelector('.range-active-background');

      // Validation check
      if (!this.minRangeSlider || !this.maxRangeSlider || 
          !this.minNumberInput || !this.maxNumberInput) {
        console.error('PriceRange: Required elements not found');
        return;
      }

      // Store the maximum possible value
      this.absoluteMax = Number(this.minRangeSlider.getAttribute('max'));
      
      // Ensure max attribute is set as number
      this.minRangeSlider.max = this.absoluteMax;
      this.maxRangeSlider.max = this.absoluteMax;

      // Debounce timer for form submission
      this.submitTimer = null;

      // Initialize
      this.init();
    }

    init() {
      this.attachEventListeners();
      this.updateVisualState();
    }

    attachEventListeners() {
      // Range sliders - real-time updates
      this.minRangeSlider.addEventListener('input', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.onMinSliderInput(Number(e.target.value));
      });

      this.maxRangeSlider.addEventListener('input', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.onMaxSliderInput(Number(e.target.value));
      });

      // Submit when user releases slider
      this.minRangeSlider.addEventListener('change', () => {
        this.triggerFormSubmit();
      });

      this.maxRangeSlider.addEventListener('change', () => {
        this.triggerFormSubmit();
      });

      // Number inputs
      this.minNumberInput.addEventListener('keydown', (e) => this.validateKeyPress(e));
      this.maxNumberInput.addEventListener('keydown', (e) => this.validateKeyPress(e));

      this.minNumberInput.addEventListener('input', (e) => {
        this.onMinInputChange(e.target.value);
      });

      this.maxNumberInput.addEventListener('input', (e) => {
        this.onMaxInputChange(e.target.value);
      });

      this.minNumberInput.addEventListener('blur', () => {
        this.validateMinInput();
        this.triggerFormSubmit();
      });

      this.maxNumberInput.addEventListener('blur', () => {
        this.validateMaxInput();
        this.triggerFormSubmit();
      });
    }

    onMinSliderInput(value) {
      let newMin = value;
      const currentMax = Number(this.maxRangeSlider.value);

      if (newMin > currentMax) {
        newMin = currentMax;
        this.minRangeSlider.value = newMin;
      }

      this.minNumberInput.value = newMin;
      this.updateVisualState();
    }

    onMaxSliderInput(value) {
      let newMax = value;
      const currentMin = Number(this.minRangeSlider.value);

      if (newMax < currentMin) {
        newMax = currentMin;
        this.maxRangeSlider.value = newMax;
      }

      this.maxNumberInput.value = newMax;
      this.updateVisualState();
    }

    onMinInputChange(value) {
      let newMin = this.parseNumber(value);
      this.minRangeSlider.value = newMin;
      this.updateVisualState();
      this.debouncedFormSubmit();
    }

    onMaxInputChange(value) {
      let newMax = this.parseNumber(value);
      this.maxRangeSlider.value = newMax;
      this.updateVisualState();
      this.debouncedFormSubmit();
    }

    validateMinInput() {
      let value = this.parseNumber(this.minNumberInput.value);
      const maxValue = Number(this.maxNumberInput.value);

      if (value < 0) value = 0;
      if (value > maxValue) value = maxValue;
      if (value > this.absoluteMax) value = this.absoluteMax;

      this.minNumberInput.value = value;
      this.minRangeSlider.value = value;
      this.updateVisualState();
    }

    validateMaxInput() {
      let value = this.parseNumber(this.maxNumberInput.value);
      const minValue = Number(this.minNumberInput.value);

      if (value < minValue) value = minValue;
      if (value > this.absoluteMax) value = this.absoluteMax;
      if (value < 0) value = 0;

      this.maxNumberInput.value = value;
      this.maxRangeSlider.value = value;
      this.updateVisualState();
    }

    validateKeyPress(event) {
      if (event.metaKey || event.ctrlKey) return;

      const allowedKeys = [
        'Tab', 'Backspace', 'Delete', 'Enter', 'Escape',
        'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
        'Home', 'End'
      ];

      const allowedChars = /[0-9.]/;

      if (!allowedKeys.includes(event.key) && !allowedChars.test(event.key)) {
        event.preventDefault();
      }
    }

    updateVisualState() {
      const minValue = Number(this.minRangeSlider.value);
      const maxValue = Number(this.maxRangeSlider.value);

      const minPercent = (minValue / this.absoluteMax) * 100;
      const maxPercent = (maxValue / this.absoluteMax) * 100;

      if (this.activeBackground) {
        this.activeBackground.style.left = `${minPercent}%`;
        this.activeBackground.style.width = `${maxPercent - minPercent}%`;
      }
    }

    debouncedFormSubmit() {
      clearTimeout(this.submitTimer);
      this.submitTimer = setTimeout(() => {
        this.triggerFormSubmit();
      }, 800);
    }

    triggerFormSubmit() {
      // CRITICAL FIX: Check for both desktop and mobile form parents
      const facetFormDesktop = this.closest('facet-filters-form');
      const facetFormMobile = this.closest('facet-filters-form-mobile');
      
      const facetForm = facetFormDesktop || facetFormMobile;
      
      if (!facetForm) {
        console.warn('PriceRange: No facet form parent found (neither desktop nor mobile)');
        return;
      }

      // Get the actual form element
      const formElement = facetForm.querySelector('form');
      
      if (!formElement) {
        console.warn('PriceRange: No form element found');
        return;
      }

      // Create search params from form data
      const formData = new FormData(formElement);
      const searchParams = new URLSearchParams(formData).toString();

      // Trigger the facet form submission
      if (typeof facetForm.onSubmitForm === 'function') {
        facetForm.onSubmitForm(searchParams, { target: this.minNumberInput });
      } else {
        console.warn('PriceRange: onSubmitForm method not found on parent');
      }
    }

    parseNumber(value) {
      const parsed = Number(value);
      return isNaN(parsed) ? 0 : parsed;
    }
  }

  customElements.define('price-range', PriceRange);
}